import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/crm/ProtectedRoute";
import Layout from "@/components/crm/Layout";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import LeadFilters from "@/pages/LeadFilters";
import LeadDetail from "@/pages/LeadDetail";
import Pipeline from "@/pages/Pipeline";
import FollowUps from "@/pages/FollowUps";
import Tasks from "@/pages/Tasks";
import Appointments from "@/pages/Appointments";
import Patients from "@/pages/Patients";
import PatientDetail from "@/pages/PatientDetail";
import Branches from "@/pages/Branches";
import Franchises from "@/pages/Franchises";
import Reports from "@/pages/Reports";
import SourcePerformance from "@/pages/SourcePerformance";
import Users from "@/pages/Users";
import AuditLogs from "@/pages/AuditLogs";
import Integrations from "@/pages/Integrations";
import MasterData from "@/pages/MasterData";
import Packages from "@/pages/Packages";
import ClinicSettings from "@/pages/ClinicSettings";
import OpsReports from "@/pages/OpsReports";
import Communications from "@/pages/Communications";
import Recalls from "@/pages/Recalls";
import Checkout from "@/pages/Checkout";
import CheckIn from "@/pages/CheckIn";
import DoctorMyDay from "@/pages/DoctorMyDay";
import Consultation from "@/pages/Consultation";
import CalendarPage from "@/pages/CalendarPage";
import BookAppointment from "@/pages/BookAppointment";
import Invoices from "@/pages/Invoices";
import CreateInvoice from "@/pages/CreateInvoice";
import DoctorAnalytics from "@/pages/DoctorAnalytics";
import NewLead from "@/pages/NewLead";
import WhatsAppInbox from "@/pages/WhatsAppInbox";
import MetaOAuthCallback from "@/pages/MetaOAuthCallback";

const page = (title, Component) => (
  <ProtectedRoute>
    <Layout title={title}>
      <Component />
    </Layout>
  </ProtectedRoute>
);

// Full-screen route: authenticated but WITHOUT the app sidebar/topbar chrome, so
// the page owns the entire viewport (e.g. the Filter Leads workspace).
const fullPage = (Component) => (
  <ProtectedRoute>
    <Component />
  </ProtectedRoute>
);

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={page("Dashboard", Dashboard)} />
          <Route path="/leads" element={page("Leads", Leads)} />
          <Route path="/leads/filters" element={fullPage(LeadFilters)} />
          <Route path="/leads/new" element={page("New Lead", NewLead)} />
          <Route path="/leads/:id" element={page("Lead Details", LeadDetail)} />
          <Route path="/pipeline" element={page("Sales Pipeline", Pipeline)} />
          <Route path="/followups" element={page("Follow-ups", FollowUps)} />
          <Route path="/whatsapp" element={page("WhatsApp", WhatsAppInbox)} />
          <Route path="/tasks" element={page("Tasks", Tasks)} />
          <Route path="/calendar" element={page("Calendar", CalendarPage)} />
          <Route path="/appointments" element={page("Appointments", Appointments)} />
          <Route path="/appointments/book" element={page("Book Appointment", BookAppointment)} />
          <Route path="/patients" element={page("Patients", Patients)} />
          <Route path="/patients/:id" element={page("Patient Profile", PatientDetail)} />
          <Route path="/branches" element={page("Branches", Branches)} />
          <Route path="/franchises" element={page("Franchises", Franchises)} />
          <Route path="/reports" element={page("Reports & Analytics", Reports)} />
          {/* Revenue is now a sub-tab of Reports; keep old bookmarks working. */}
          <Route path="/reports/revenue" element={<Navigate to="/reports" replace />} />
          <Route path="/performance-source" element={page("Performance Source", SourcePerformance)} />
          <Route path="/users" element={page("Access Control", Users)} />
          <Route path="/audit" element={page("Audit Logs", AuditLogs)} />
          <Route path="/integrations" element={page("Integrations", Integrations)} />
          <Route path="/integrations/meta/callback" element={<MetaOAuthCallback />} />
          <Route path="/recalls" element={page("Recalls", Recalls)} />
          <Route path="/checkin/:appointmentId" element={page("Check In", CheckIn)} />
          <Route path="/checkout/:appointmentId" element={page("Checkout", Checkout)} />
          <Route path="/packages" element={page("Treatment Packages", Packages)} />
          <Route path="/clinic-settings" element={page("Clinic Settings", ClinicSettings)} />
          <Route path="/ops-reports" element={page("Clinic Reports", OpsReports)} />
          <Route path="/communications" element={page("Patient Messaging", Communications)} />
          <Route path="/master-data" element={page("Master Data", MasterData)} />
          <Route path="/doctor/my-day" element={page("My Day", DoctorMyDay)} />
          <Route path="/consultation/:appointmentId" element={page("Consultation", Consultation)} />
          <Route path="/invoices" element={page("Invoices", Invoices)} />
          <Route path="/invoices/create" element={page("Create Invoice", CreateInvoice)} />
          <Route path="/doctor-analytics" element={page("Doctor Analytics", DoctorAnalytics)} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
